import React, { useState, useEffect } from 'react';

export const TrainInfoPanel = ({
  train,
  hasProfile = true,
  hasClockAndMode = true,
  isTrainConnected = true,
  topologyUpdatedTime = null,
  syncStatus = null,
  packagesForDelete = [],
  deleteModalOpen = false,
  hasPackageActivation = false,
  filteredPackages = [],
  onOpenDeletePackagesModal = () => {},
  onOpenActivationModal = () => {},
  onExportTopology = () => {},
  showTopologyDownload = false,
  formatTimestampHoursUnix,
  operationMode
}) => {
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    let offlineTimer;
    
    if (!train || !train.status) {
      // Set a timer to show "Train is Offline" after 3 seconds
      offlineTimer = setTimeout(() => {
        setIsOffline(true);
      }, 3000);
    } else {
      // Reset offline state if train data becomes available
      setIsOffline(false);
    }

    // Clean up timer when component unmounts or train data changes
    return () => {
      clearTimeout(offlineTimer);
    };
  }, [train]);

  if (!train || !train.status) {
    return (
      <div className="info-panel">
        {isOffline ? (
          <div className="info-panel__offline">
            <i className="fa fa-exclamation-triangle"></i>
            <span>Train is Offline</span>
          </div>
        ) : (
          <div className="info-panel__loading">
            <i className="fa fa-spinner fa-spin"></i>
            <span>Loading train information...</span>
          </div>
        )}
      </div>
    );
  }
  
  // Equivalent of operationModeIconfilter
  const getOperationModeIconClass = (mode) => {
    let iconFaClass = '';
    if (mode === 'ON_DEPOT') {
      iconFaClass = 'fa-warehouse';
    } else if (mode === 'ON_MAINTENANCE' || mode === 'ON_DEPOT_MAINTENANCE') {
      iconFaClass = 'fa-tools';
    } else if (mode === 'IN_SERVICE' || mode === 'OUT_OF_SERVICE') {
      iconFaClass = 'fa-train train-icon-in-out-service';
    } else if (mode === 'UNKNOWN') {
      iconFaClass = 'fa-question-circle';
    }
    return iconFaClass;
  };
  
  return (
    <div className="info-panel">
 
      
      {/* Train ID */}
      <h3 className="info-panel__train-text">
        <i className="fa fa-train train-icon fa-md"></i>
        {hasProfile ? ' Train ' : ' Train No. '}
        <span id="train-id-text" className="info-panel__train-title">
          {train.status.trainId}
        </span>
      </h3>
      
      {/* Operation Mode */}
      {train && hasClockAndMode && (
        <h3 className="info-panel__operation-mode">
          Operation mode&nbsp;
          <span className="info-panel__operation-mode__subtitle">
            {train.status.mode === 'ON_DEPOT_MAINTENANCE' && <i className="fa fa-warehouse"></i>}
            <i className={`fa ${getOperationModeIconClass(train.status.mode)}`}></i>
            {train.status.mode === 'IN_SERVICE' && <i className="fa fa-users"></i>}
            {train.status.mode === 'OUT_OF_SERVICE' && <i className="fa fa-users-slash"></i>}
            {operationMode(train.status.mode)}
          </span>
        </h3>
      )}
      
      {/* Connection Status */}
      {!isTrainConnected && (
        <h3 className="info-panel__operation-mode">
          <span className="info-panel__operation-mode__disconnected">Train disconnected</span>
        </h3>
      )}
      
      {/* Topology Updated Time */}
      {isTrainConnected && topologyUpdatedTime && (
        <h3 className="info-panel__topology-update">
          Topology updated
          <span className="info-panel__topology-update__subtitle">{topologyUpdatedTime}</span>
        </h3>
      )}
      
      {/* Sync Status */}
      {syncStatus && train.status.mode === 'ON_DEPOT' && (
        <h3 className="info-panel__sync-status">
          Sync status
          {syncStatus !== 'In Progress' ? (
            <span className="info-panel__sync-status__disconnected">
              {syncStatus}
              <span>
                <i 
                  className="fa fa-exclamation-triangle" 
                  title="Auto sync ongoing, manual file transfer is slow!"
                ></i>
              </span>
            </span>
          ) : (
            <span className="info-panel__sync-status__subtitle">{syncStatus}</span>
          )}
        </h3>
      )}
      
      {/* Train Clock */}
      {hasClockAndMode && (
        <h3 className="info-panel__train-clock">
          <i className="fa fa-clock clock-icon"></i> Train clock
          <span className="info-panel__operation-mode__subtitle">
            {formatTimestampHoursUnix(train.status.trainClock, train.status.timezone)}
          </span>
        </h3>
      )}
      
      {/* Packages for Deletion - Only shown if packages and handler exist */}
      {packagesForDelete?.length > 0 && deleteModalOpen && onOpenDeletePackagesModal !== (() => {}) && (
        <div className="info-panel__activation-modal">
          <span>Packages pending deletion</span>
          <button 
            className="toolbar-button-io content-area__update-text" 
            onClick={onOpenDeletePackagesModal}
          >
            Open list
          </button>
        </div>
      )}
      
      {/* Group action buttons together */}
      <div className="info-panel__actions-container">
        {/* Packages for Activation - Only shown if packages and handler exist */}
        {hasPackageActivation && filteredPackages?.length > 0 && onOpenActivationModal !== (() => {}) && (
          <div className="info-panel__activation-modal">
            <span>Packages available for activation</span>
            <button 
              className="toolbar-button-io content-area__update-text" 
              onClick={onOpenActivationModal}
            >
              Open modal
            </button>
          </div>
        )}
        
        {/* Topology Download - Only shown if explicitly enabled */}
        {showTopologyDownload === true && (
          <div className="info-panel__topology-download">
            <button 
              id="download-topology-button" 
              data-testid="download-topology-button"
              className="toolbar-button-io" 
              onClick={onExportTopology}
            >
              <i className="fas fa-download fa-fw" aria-hidden="true" /> Download topology
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
